// Fest-Mails Herbstfest: Festwetter (Do 22.10. 09:00) + Dank mit Frühlingsrabatt (Mo 26.10. 10:00) – gemeinsame Gästeliste, Versand nur nach Freigabe
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { CloudSun, Loader2, Save, Send, ExternalLink, CheckCircle2, Undo2, Heart } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/festwetter`;
const err = (e, f) => e.response?.data?.detail || f;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short", timeZone: "Europe/Berlin" }) : "–");
const btn = "flex items-center gap-2 text-xs px-3 py-1.5 rounded-md border border-white/10 hover:bg-white/5 transition-colors disabled:opacity-50";
const area = "w-full bg-black/30 border border-white/10 rounded-md p-2 text-xs font-mono focus:outline-none focus:border-eb-primary";
const STATUS = { entwurf: ["Entwurf – noch nicht freigegeben", "#F59E0B"], freigegeben: ["Freigegeben", "#10B981"],
  "wird gesendet": ["Wird gerade verschickt", "#38BDF8"], versendet: ["Verschickt", "#10B981"] };
const WD = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
const GUESTS_CHANGED = "fest-guests-changed";

function Forecast({ s }) {
  if (!s.days.length) return <p className="text-xs text-amber-300" data-testid="fw-no-forecast">Vorhersage fürs Festwochenende noch nicht verfügbar.</p>;
  return (
    <div data-testid="fw-forecast">
      <div className="grid grid-cols-3 gap-2">
        {s.days.map((d) => {
          const dt = new Date(`${d.date}T12:00:00`);
          return (
            <div key={d.date} className={`bg-black/25 rounded-lg p-2 text-center ${d.date === "2026-10-24" ? "ring-1 ring-amber-400" : ""}`}>
              <div className="text-[11px] text-eb-muted">{WD[dt.getDay()]} {dt.getDate()}.{dt.getMonth() + 1}.</div>
              <div className="text-sm font-semibold">{d.tmax}° <span className="text-eb-muted font-normal">/ {d.tmin}°</span></div>
              <div className="text-[11px] text-eb-muted">Regen {d.rain_prob ?? "–"} %{d.sun_h != null ? ` · Sonne ${d.sun_h} h` : ""}</div>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-eb-muted mt-1">Stand {s.weather_at} · beim Versand wird die Vorhersage neu abgerufen</p>
      <ul className="text-xs list-disc pl-5 mt-2 space-y-0.5" data-testid="fw-tips">{s.tips.map((t) => <li key={t}>{t}</li>)}</ul>
    </div>
  );
}

function Guests({ s, locked, reload, setMsg }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [invalid, setInvalid] = useState([]);
  const save = async () => {
    if (!window.confirm("Die Liste gilt für Festwetter und Dank – vorhandene Freigaben werden zurückgenommen. Weiter?")) return;
    setBusy(true);
    try {
      const r = await axios.put(`${API}/guests`, { text });
      setInvalid(r.data.invalid); setMsg(`${r.data.total} Gäste gespeichert`); setText(""); window.dispatchEvent(new Event(GUESTS_CHANGED));
    }
    catch (e) { setMsg(err(e, "Liste nicht gespeichert")); }
    setBusy(false);
  };
  return (
    <div className="space-y-2">
      <div className="text-xs"><b data-testid="fw-guest-count">{s.guests.length} Gäste</b>
        <span className="text-eb-muted"> {s.guests.slice(0, 6).map((g) => g.name || g.email).join(", ")}{s.guests.length > 6 ? ` … +${s.guests.length - 6}` : ""}</span></div>
      {!locked && <>
        <label className="block text-[11px] text-eb-muted uppercase tracking-wider" htmlFor="fw-guests">Gästeliste einfügen</label>
        <textarea id="fw-guests" className={area} rows={4} value={text} onChange={(e) => setText(e.target.value)} data-testid="fw-guests-input"
          placeholder={"Eine Adresse pro Zeile, z. B. aus dem easybooking-Export:\nAnna Muster <anna@beispiel.de>\nMax Meyer;max@beispiel.de"} />
        <button className={btn} disabled={busy || !text.trim()} onClick={save} data-testid="fw-guests-save">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Liste speichern (ersetzt die bisherige)</button>
      </>}
      {invalid.length > 0 && <p className="text-[11px] text-amber-300" data-testid="fw-guests-invalid">Ohne E-Mail übersprungen: {invalid.join(" · ")}</p>}
    </div>
  );
}

function Note({ s, locked, reload, setMsg }) {
  const [note, setNote] = useState(s.note || "");
  useEffect(() => { setNote(s.note || ""); }, [s.note]);
  const save = async () => {
    if (s.approved && !window.confirm("Der Text ändert sich – die Freigabe wird dabei zurückgenommen. Weiter?")) return;
    try { await axios.put(`${API}/${s.cid}/note`, { note }); setMsg("Persönliche Zeile gespeichert"); reload(); }
    catch (e) { setMsg(err(e, "Nicht gespeichert")); }
  };
  return (
    <div className="space-y-2">
      <label className="block text-[11px] text-eb-muted uppercase tracking-wider" htmlFor={`fw-note-${s.cid}`}>Persönliche Zeile (optional)</label>
      <textarea id={`fw-note-${s.cid}`} className={area} rows={2} value={note} disabled={locked} onChange={(e) => setNote(e.target.value)} data-testid={`fw-${s.cid}-note-input`}
        placeholder="Optional: persönliche Zeile, z. B. „Check-in ab 15 Uhr an der Rezeption“" />
      {!locked && <button className={btn} onClick={save} data-testid={`fw-${s.cid}-note-save`}><Save size={13} /> Zeile speichern</button>}
    </div>
  );
}

function Actions({ s, locked, reload, setMsg }) {
  const [to, setTo] = useState("fh@schwalbennest-ruegen.de");
  const [busy, setBusy] = useState("");
  const call = async (k, fn, ok) => {
    setBusy(k);
    try { await fn(); setMsg(ok); reload(); } catch (e) { setMsg(err(e, "Fehlgeschlagen")); }
    setBusy("");
  };
  const approve = (v) => {
    const what = s.cid === "danke" ? "Dank-Mail mit Frühlingsrabatt" : "Festwetter-Hinweis";
    const q = v ? `${what} an ${s.guests.length} Gäste freigeben? Versand automatisch ${s.send_label}.` : "Freigabe zurücknehmen?";
    if (window.confirm(q)) call("approve", () => axios.post(`${API}/${s.cid}/approve`, { approved: v }), v ? "Freigegeben – Versand zur geplanten Zeit" : "Freigabe zurückgenommen");
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <a className={btn} href={`${API}/${s.cid}/preview`} target="_blank" rel="noreferrer" data-testid={`fw-${s.cid}-preview`}><ExternalLink size={13} /> Vorschau</a>
      <input className="bg-black/30 border border-white/10 rounded-md px-2 py-1.5 text-xs w-56" value={to} onChange={(e) => setTo(e.target.value)} data-testid={`fw-${s.cid}-test-email`} />
      <button className={btn} disabled={!!busy} onClick={() => call("test", () => axios.post(`${API}/${s.cid}/test`, { email: to }), `Testmail an ${to} verschickt`)} data-testid={`fw-${s.cid}-test-send`}>
        {busy === "test" ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Testmail</button>
      {!locked && (s.approved
        ? <button className={btn} disabled={!!busy} onClick={() => approve(false)} data-testid={`fw-${s.cid}-unapprove`}><Undo2 size={13} /> Freigabe zurücknehmen</button>
        : <button className={`${btn} border-emerald-400/40 text-emerald-300`} disabled={!!busy || !s.guests.length} onClick={() => approve(true)} data-testid={`fw-${s.cid}-approve`}>
          <CheckCircle2 size={13} /> Freigeben</button>)}
    </div>
  );
}

function Voucher({ s }) {
  const v = s.voucher;
  return (
    <div className="bg-black/25 rounded-lg p-3 text-xs space-y-1" data-testid="fw-danke-voucher">
      <div><b className="text-amber-300 text-sm">{v.percent} % Frühlingsrabatt</b> · Aufenthalte {v.period} · Code <b className="font-mono">{v.code}</b></div>
      <div className="text-eb-muted">Gäste fragen über ihre eigene Frühlingsseite an (Link in der Mail, Code schon eingetragen) – das zählt automatisch. Buchungen per Telefon/E-Mail im Rückblick abhaken; in easybooking den Rabatt von Hand abziehen.</div>
      <div data-testid="fw-danke-guests"><b>{s.guests.length} Gäste</b> <span className="text-eb-muted">aus der Gästeliste im Festwetter-Hinweis</span></div>
    </div>
  );
}

const META = {
  wetter: { icon: CloudSun, title: "Festwetter-Hinweis · Herbstfest 2026", testid: "festwetter-panel",
    text: "Persönliche E-Mail mit Wettervorhersage (Fr–So) und Packtipps an alle Gäste der Liste" },
  danke: { icon: Heart, title: "Danke nach dem Fest · Frühlingsrabatt", testid: "danke-panel",
    text: "Persönlicher Dank fürs Mitfeiern mit Rabatt-Code für den Frühling an alle Gäste der Liste" },
};

function FestMailPanel({ cid }) {
  const [s, setS] = useState(null);
  const [msg, setMsg] = useState("");
  const load = useCallback(() => axios.get(`${API}/${cid}`).then((r) => setS(r.data)).catch((e) => setMsg(err(e, "Fest-Mail nicht ladbar"))), [cid]);
  useEffect(() => {
    load();
    window.addEventListener(GUESTS_CHANGED, load);
    return () => window.removeEventListener(GUESTS_CHANGED, load);
  }, [load]);
  if (!s) return msg ? <div className="card text-sm text-rose-400">{msg}</div> : null;
  const m = META[cid];
  const locked = ["wird gesendet", "versendet"].includes(s.status);
  const [label, color] = STATUS[s.status] || STATUS.entwurf;
  return (
    <div className="card space-y-4" data-testid={m.testid}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="overline flex items-center gap-1"><m.icon size={12} /> {m.title}</div>
        <span className="channel-pill" style={{ background: `${color}22`, color }} data-testid={`fw-${cid}-status`}>{label}</span>
      </div>
      <p className="text-xs text-eb-muted">{m.text} – <b>{s.send_label}</b>, nur wenn du vorher freigibst. Absender fh@schwalbennest-ruegen.com, Antworten an {s.reply_to}.</p>
      {cid === "wetter" ? <><Forecast s={s} /><Guests s={s} locked={false} reload={load} setMsg={setMsg} /></> : <Voucher s={s} />}
      <Note s={s} locked={locked} reload={load} setMsg={setMsg} />
      <Actions s={s} locked={locked} reload={load} setMsg={setMsg} />
      {s.approved && !locked && <p className="text-[11px] text-emerald-300" data-testid={`fw-${cid}-approved-by`}>Freigegeben {fmt(s.approved_at)} von {s.approved_by}</p>}
      {s.status === "versendet" && <p className="text-xs text-emerald-300" data-testid={`fw-${cid}-result`}>Verschickt {fmt(s.sent_at)}: {s.sent_count} von {s.results.length} zugestellt
        {s.results.some((r) => !r.sent) ? ` · nicht zugestellt: ${s.results.filter((r) => !r.sent).map((r) => r.email).join(", ")}` : ""}</p>}
      {s.test && <p className="text-[11px] text-eb-muted" data-testid={`fw-${cid}-test-last`}>Letzte Testmail {fmt(s.test.at)} an {s.test.to}</p>}
      {msg && <p className="text-xs text-eb-muted" data-testid={`fw-${cid}-msg`}>{msg}</p>}
    </div>
  );
}

export const FestwetterPanel = () => <FestMailPanel cid="wetter" />;
export const DankePanel = () => <FestMailPanel cid="danke" />;
